import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../../services/adminApi';
import { HiOutlineSave } from 'react-icons/hi';

const FIELDS = [
  { name: 'brand_name', label: 'Brand Name', type: 'text' },
  { name: 'tagline', label: 'Tagline', type: 'text' },
  { name: 'hero_title', label: 'Hero Title', type: 'text' },
  { name: 'hero_subtitle', label: 'Hero Subtitle', type: 'text' },
  { name: 'hero_description', label: 'Hero Description', type: 'textarea' },
  { name: 'hero_cta_text', label: 'Hero CTA Text', type: 'text' },
  { name: 'hero_cta_link', label: 'Hero CTA Link', type: 'text' },
  { name: 'hero_secondary_text', label: 'Hero Secondary Text', type: 'text' },
  { name: 'hero_secondary_link', label: 'Hero Secondary Link', type: 'text' },
  { name: 'promo_label', label: 'Promo Label', type: 'text' },
  { name: 'promo_title', label: 'Promo Title', type: 'text' },
  { name: 'promo_description', label: 'Promo Description', type: 'textarea' },
  { name: 'promo_cta_text', label: 'Promo CTA Text', type: 'text' },
  { name: 'promo_cta_link', label: 'Promo CTA Link', type: 'text' },
  { name: 'base_currency', label: 'Base Currency', type: 'text' },
  { name: 'footer_tagline', label: 'Footer Tagline', type: 'text' },
];

const AdminSiteSettings: React.FC = () => {
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        const data = res.data;
        const values: Record<string, string> = {};
        FIELDS.forEach((f) => {
          values[f.name] = data[f.name] || '';
        });
        setForm(values);
      } catch {
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateSettings(form);
      setMessage('Settings saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const msgs = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
        setError(msgs);
      } else {
        setError('Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-spinner" />;

  return (
    <div>
      <h1 className="admin-page-title">Site Settings</h1>

      {message && <div className="admin-alert admin-alert-success">{message}</div>}
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-card">
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form-grid">
            {FIELDS.map((field) => (
              <div
                key={field.name}
                className={`admin-form-group ${field.type === 'textarea' ? 'admin-form-group-full' : ''}`}
              >
                <label>{field.label}</label>
                {field.type === 'textarea' ? (
                  <textarea
                    name={field.name}
                    value={form[field.name] || ''}
                    onChange={handleChange}
                    rows={3}
                  />
                ) : (
                  <input
                    name={field.name}
                    value={form[field.name] || ''}
                    onChange={handleChange}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="admin-form-actions">
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              <HiOutlineSave size={18} />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSiteSettings;
